import {
  IColumnTypes,
  ISpreadsheetContextMenuState,
  ISpreadsheetData,
  rowHeights,
  IFilterType,
  ISelectedCellRange,
  ISelectedCellState,
  ISelectedSearchCell
} from 'components/spreadsheet/types'
import { IMatrixSettings, ITransposeSettings, SelectKindOptions } from 'types'

export const EMPTY_TRANSPOSE_SETTINGS: ITransposeSettings = {
  pivotColumnId: null,
  fieldColumnWidth: null,
  dataColumnWidths: null
}

export const EMPTY_MATRIX_SETTINGS: IMatrixSettings = {
  pivotColumnId: null,
  rowLabelColumnIds: [],
  dataFieldColumnIds: [],
  fieldColumnWidth: null,
  dataColumnWidths: null
}

export const DEFAULT_MATRIX_COLUMN_WIDTH = 150

// Height, in px, a newly inserted embedded table, dashboard row or iframe starts at in a Document.
// Editors resize it afterwards by dragging its bottom edge.
export const DEFAULT_EMBED_HEIGHT = 500

/***

  Spreadsheet Constants
  ---------------------------

  - KEY_PRESS - code to identify which key is being pressed
  - KEY_PRESSES - the list of key presses which should be activated in the component

***/

export const KEY_PRESS = {
  left: 'ArrowLeft',
  up: 'ArrowUp',
  right: 'ArrowRight',
  down: 'ArrowDown',
  esc: 'Escape',
  pageUp: 'PageUp',
  pageDown: 'PageDown',
  delete: 'Delete',
  backspace: 'Backspace'
}

export const KEY_PRESSES = [KEY_PRESS.left, KEY_PRESS.up, KEY_PRESS.right, KEY_PRESS.down]

export enum ViewTypes {
  SPREADSHEET = 0,
  FORM = 1,
  CHART = 2,
  TRANSPOSE = 3,
  CALENDAR = 4,
  MATRIX = 5,
  CUSTOM_VIEW = 6
}

export enum ValidationDisplayTypes {
  SHOW_VALIDATION = 0,
  HIDE_VALIDATION = 1,
  ONLY_VALIDATION = 2
}

export enum CommentDisplayTypes {
  SHOW_ALL = 0,
  SHOW_WITH_COMMENTS = 1,
  SHOW_UNRESOLVED = 2,
  SHOW_RESOLVED = 3
}

export const ViewTypesNames = ['Spreadsheet', 'Form', 'Chart', 'Transpose', 'Calendar', 'Matrix', 'Custom']

export const ALLOWED_VIEW_TYPES_ON_SELECT_OPTIONS = [ViewTypes.SPREADSHEET]

/** View types where columns are derived from data (synthetic IDs that the backend can't persist). */
export const SYNTHETIC_COLUMN_VIEW_TYPES = new Set([ViewTypes.TRANSPOSE, ViewTypes.MATRIX])

export const hasSyntheticColumns = (viewType: ViewTypes | number): boolean =>
  SYNTHETIC_COLUMN_VIEW_TYPES.has(viewType as ViewTypes)

/**
 * View types that actually render row grouping / tree-group view. Other view types (forms,
 * charts, calendars) can inherit `groupSettings` / `treeGroupView` from the source view, but
 * never render them — so the group/tree-leaf hide-protection must not apply there, otherwise
 * those columns can never be hidden (issue #10264: forms created from a tree-group view).
 */
export const GROUPING_VIEW_TYPES = new Set([ViewTypes.SPREADSHEET])

export const viewSupportsGrouping = (viewType: ViewTypes | number): boolean =>
  GROUPING_VIEW_TYPES.has(viewType as ViewTypes)

export const getInitialSpreadsheetState = (): ISpreadsheetData => {
  return {
    tableDetails: {
      publicId: '',
      name: '',
      permissionLevel: -1,
      logo: null,
      permissions: [[], [], [], [], []],
      columns: [],
      defaultViewId: '',
      type: '',
      joins: [],
      keepValidationsInSync: false,
      keepColoursInSync: false,
      duplicatedChildren: [],
      deletedAt: null,
      isDeleted: false,
      isSynced: false,
      syncProvider: null,
      syncType: null,
      isSyncing: false,
      lastSync: undefined,
      syncHourlyFrequency: 0,
      allowComments: true,
      failedSyncAttempts: 0,
      syncProjectIds: [],
      syncViewIds: [],
      syncAllHubs: false,
      projectPublicId: ''
    },
    viewDetails: {
      columns: [],
      publicId: '',
      name: '',
      default: false,
      filterSettings: [],
      groupSettings: [],
      colourSettings: [],
      rowLockSettings: [],
      hiddenColumns: [],
      sortSettings: [],
      chartSettings: null,
      transposeSettings: null,
      matrixSettings: null,
      calendarSettings: null,
      permissionLevel: -1,
      frozenIndex: -1,
      rowHeight: rowHeights.Small,
      type: ViewTypes.SPREADSHEET,
      description: null,
      isDefault: false,
      disableNewRow: false,
      disableSyncCsv: false,
      allowContributorDelete: false,
      displayValidationErrorRows: ValidationDisplayTypes.SHOW_VALIDATION,
      displayCommentRows: CommentDisplayTypes.SHOW_ALL,
      collapsedGroupView: false,
      unpackMultiselectGroupView: false,
      treeGroupView: false,
      tablePublicId: '',
      isPersonalView: false,
      configurationParentView: null,
      personalViewOwner: null
    },
    userConfiguration: {
      filterSettings: [],
      groupSettings: [],
      colourSettings: [],
      hiddenColumns: [],
      sortSettings: [],
      chartSettings: null,
      calendarSettings: null,
      rowLockSettings: [],
      unsavedChanges: false
    },
    processId: undefined,
    processSectionId: undefined,
    tableView: [],
    activeTableView: '',
    comments: [],
    rows: [],
    visibleBitmap: null,
    visibleCount: 0,
    totalRows: 0,
    totalRowsOnServer: 0,
    loading: true,
    loadingProgress: 0,
    rowStreamComplete: false,
    streamPendingDeletes: null,
    streamPendingUpdates: null,
    refreshing: false,
    busyLabel: null,
    error: false,
    errorMessage: '',
    columnWidths: {
      frozenWidth: 100,
      unfrozenWidth: 100
    },
    isAdmin: false,
    isContributor: false,
    collapsedGroupIds: new Set<string>(),
    collapsedColumnIds: new Set<string>(),
    originalViewColumns: [],
    viewContext: null,
    processTags: [],
    processVariables: [],
    processVariableValues: [],
    searchTerm: '',
    searchFound: 0,
    selectedSearchCell: { rowId: null, columnId: null, rowNumber: -1, columnNumber: -1 },
    columnValuesCount: {},
    columnIdToIndex: new Map<string, number>(),
    indexToColumnId: [],
    columnStore: null,
    dataVersion: 0,
    searchedRows: new Map<string, string[]>(),
    history: [],
    isPollingEnabled: false,
    historyPoint: -1,
    templateRender: false
  }
}

export const INITIAL_SPREADSHEET_CONTEXT_MENU_STATE: ISpreadsheetContextMenuState = {
  open: false,
  top: '0px',
  right: '0px',
  left: '0px',
  bottom: '0px',
  columnNumber: -1,
  columnId: '',
  rowNumber: -1,
  rowId: ''
}

export const COLUMN_TYPE_NAMES = {
  text: 'Text',
  datetime: 'Date + Time',
  date: 'Date',
  link: 'URL',
  multilink: 'Multiple URL',
  select: 'Select',
  multiselect: 'Multiple Select',
  integer: 'Number',
  float: 'Decimal',
  percentage: 'Percentage',
  tag: 'Tag',
  variable: 'Variable',
  attachment: 'Attachment',
  phone: 'Phone',
  email: 'Email',
  vote: 'Vote',
  checkbox: 'Checkbox',
  duration: 'Duration',
  json: 'JSON'
}

export const TEXT_COLUMN_TYPES: IColumnTypes[] = ['text', 'tag', 'variable', 'email', 'phone']
export const NUMBER_COLUMN_TYPES: IColumnTypes[] = ['integer', 'float', 'percentage', 'duration']
export const DATE_COLUMN_TYPES: IColumnTypes[] = ['date', 'datetime']
export const ATTACHMENT_COLUMN_TYPES: IColumnTypes[] = ['attachment']
export const SELECT_COLUMN_TYPES: IColumnTypes[] = ['select', 'multiselect']

// In a form, the kinds that are their own controls, with no text field's frame around them: a checkbox, and an
// attachment's drop area and list of files.
export const FRAMELESS_FORM_KINDS: IColumnTypes[] = ['checkbox', 'attachment']

// A fresh select column's options container: no manual options, no source table, nothing populated.
// Used by Edit column, for a column being added and for a column changed to a select.
export const DEFAULT_KIND_OPTIONS: SelectKindOptions = {
  publicId: '',
  manualOptions: [],
  tableOptions: {
    cachedOptions: [],
    tableId: '',
    viewId: '',
    columnId: '',
    dependencies: [],
    liveValues: false
  },
  autopopulate: false,
  expandOptionsViaCellEntry: true
}
export const LINK_COLUMN_TYPES: IColumnTypes[] = ['link', 'multilink']
export const VOTE_COLUMN_TYPES: IColumnTypes[] = ['vote']
export const BOOLEAN_COLUMN_TYPES: IColumnTypes[] = ['checkbox']
export const WARNING_COLUMN_TYPES: IColumnTypes[] = [
  ...DATE_COLUMN_TYPES,
  ...ATTACHMENT_COLUMN_TYPES,
  ...SELECT_COLUMN_TYPES,
  ...NUMBER_COLUMN_TYPES,
  ...LINK_COLUMN_TYPES,
  ...VOTE_COLUMN_TYPES,
  ...BOOLEAN_COLUMN_TYPES
]

export const BLOCK_SELECT_OPTION_TYPES: IColumnTypes[] = ['multilink', 'vote', ...ATTACHMENT_COLUMN_TYPES, 'json']
export const EDITABLE_DATETIME_OPTION_TYPES: IColumnTypes[] = ['date', 'datetime']
export const INPUT_COLUMN_OPTION_TYPES = [...TEXT_COLUMN_TYPES, 'link', ...NUMBER_COLUMN_TYPES, 'date', 'datetime']
export const BLOCK_COPY_PASTE_CUT_COLUMN_TYPES: IColumnTypes[] = [
  ...ATTACHMENT_COLUMN_TYPES,
  ...VOTE_COLUMN_TYPES,
  ...BOOLEAN_COLUMN_TYPES
]

// The type a new column gets when nothing else is chosen: what + opens Edit column on.
export const DEFAULT_COLUMN_TYPE: IColumnTypes = 'text'

export const ALL_TYPES = [
  ...TEXT_COLUMN_TYPES,
  ...NUMBER_COLUMN_TYPES,
  ...DATE_COLUMN_TYPES,
  ...ATTACHMENT_COLUMN_TYPES,
  ...SELECT_COLUMN_TYPES,
  ...LINK_COLUMN_TYPES,
  ...VOTE_COLUMN_TYPES,
  ...BOOLEAN_COLUMN_TYPES,
  // The cast is required: without it the array's element type widens to `string`,
  // which breaks kind-keyed lookups over ALL_TYPES (e.g. COLUMN_TYPE_NAMES[type]).
  'json' as IColumnTypes
]

export const BLOCK_GROUP_BY: IColumnTypes[] = ['multilink', 'vote', 'json']
export const BLOCK_SORT_BY: IColumnTypes[] = ['multilink', 'attachment', 'json']
export const BLOCK_FILTER_BY: IColumnTypes[] = ['vote']
export const BLOCK_SEARCH_BY: IColumnTypes[] = [
  'multilink',
  ...VOTE_COLUMN_TYPES,
  ...ATTACHMENT_COLUMN_TYPES,
  ...BOOLEAN_COLUMN_TYPES,
  'json'
]

export const STARTING_PAGE = 1

export const FilterTypesToDescription = {
  [IFilterType.neq]: 'is not',
  [IFilterType.eq]: 'is',
  [IFilterType.contains]: 'contains',
  [IFilterType.in]: 'is in',
  [IFilterType.gte]: '>=',
  [IFilterType.lte]: '<=',
  [IFilterType.gt]: '>',
  [IFilterType.lt]: '<',
  [IFilterType.one_of]: 'one of',
  [IFilterType.not_one_of]: 'not one of',
  [IFilterType.not_contains]: 'does not contain',
  [IFilterType.is_null]: 'is empty',
  [IFilterType.is_not_null]: 'is not empty',
  [IFilterType.is_valid]: 'is valid',
  [IFilterType.is_not_valid]: 'is not valid',
  [IFilterType.starts_with]: 'starts with',
  [IFilterType.ends_with]: 'ends with',
  [IFilterType.all_values]: 'all values'
}

export const FilterVariables = [
  '{{user.email_domain}}',
  '{{user.email}}',
  '{{user.id}}',
  '{{user.tags}}',
  '{{user.tag_values}}',
  '{{process.tags}}',
  '{{process.variables}}',
  '{{process.variable_values}}',
  '{{process.public_variables}}',
  '{{date.today}}'
]

export const COLUMN_NAME = 'column'

export const GROUP_KEY_SPLIT = '<$!?>'

export const INITIAL_SELECTED_CELL_RANGE: ISelectedCellRange = {
  endRowIndex: -1,
  endColumnIndex: -1
}

export const INITIAL_SELECTED_CELL: ISelectedCellState = {
  rowId: '',
  columnId: '',
  rowNumber: -1,
  columnNumber: -1,
  editing: false,
  value: ''
}

export const INITIAL_SELECTED_SEARCH_CELL: ISelectedSearchCell = {
  rowId: null,
  columnId: null,
  rowNumber: -1,
  columnNumber: -1
}

export const CHART_TYPES = [
  { label: 'Column', value: 'bar' },
  { label: 'Bar', value: 'horizontal-bar' },
  { label: 'Stacked Column', value: 'stacked-bar' },
  { label: 'Stacked Bar', value: 'stacked-horizontal-bar' },
  { label: 'Clustered Column', value: 'clustered-bar' },
  { label: 'Clustered Bar', value: 'clustered-horizontal-bar' },
  { label: 'S‑Curve', value: 's-curve' },
  // { label: 'Grouped Bar', value: 'grouped-bar' },
  // { label: 'Area', value: 'area' },
  { label: 'Line', value: 'line' },
  // { label: 'Multiaxis Line', value: 'multiaxis-line' },
  { label: 'Pie', value: 'pie' },
  { label: 'Doughnut', value: 'doughnut' },
  { label: 'Polar Area', value: 'polar-area' },
  { label: 'Radar', value: 'radar' },
  { label: 'Gantt', value: 'gantt' },
  { label: 'Card', value: 'card' }
  // { label: 'Scatter', value: 'scatter' },
  // { label: 'Bubble', value: 'bubble' }
]
export const CHART_AGGREGATES = [
  { label: 'Count', value: 'count' },
  { label: 'Sum', value: 'sum' },
  { label: 'Average', value: 'avg' }
]
export const CHART_DATE_AGGREGATES = [
  { label: 'Count', value: 'count' },
  { label: 'Earliest', value: 'earliest' },
  { label: 'Latest', value: 'latest' }
]

export const FILL_HANDLE_SIZE = 8
export const INDENT_PX = 20
export const BLOCK_FILL_COLUMN_TYPES: IColumnTypes[] = ['attachment', 'vote']

export const DEFAULT_FORM_COLUMN_WIDTH = 2
